import React, { useMemo, useState } from 'react';
import {
	Autocomplete,
	Box,
	Button,
	Collapse,
	FormControl,
	FormHelperText,
	FormLabel,
	Grid,
	IconButton,
	MenuItem,
	Select,
	Stack,
	TextField,
	Tooltip,
	Typography,
	Badge,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useFormikContext } from 'formik';
import FormikNoteObject from '@/components/forms/fields/FormikNoteObject';
import { InstantClientSelect } from '@/components/selects/ClientSelect';
import { InstantDealSelect } from '@/components/selects/DealSelect';
import { UserEmailSelect } from '@/components/selects/UserEmailSelect';
import { UserEmailSignatureSelect } from '@/components/selects/UserEmailSignatureSelect';
import FileUpload from '@/components/FileUpload';
import { formatDraftTimestamp } from '@/components/Drawers/AddUnlayerEmailDrawer/helpers';
import { useUnlayerUIContext } from '@/context/UnlayerUIContext';
import { useUnlayerTemplateContext } from '@/context/UnlayerTemplateContext';
import { useUnlayerDraftContext } from '@/context/UnlayerDraftContext';
import { useUnlayerComposeInteractionsContext } from '@/context/UnlayerComposeInteractionsContext';
import { useUnlayerSignatureContext } from '@/context/UnlayerSignatureContext';

/**
 * Renders the compose metadata fields and related draft/template controls for the email drawer.
 */

const ContactSelection = ({ value }) => {
	const { values, setFieldValue, setFieldTouched, isSubmitting, errors } = useFormikContext();

	return (
		<InstantClientSelect
			disabled={isSubmitting}
			showContact={true}
			attendees={values[value]}
			setClient={(contacts) => {
				setFieldValue(value, Array.isArray(contacts) ? contacts : []);
				setFieldTouched(value, true, true);
			}}
			error={errors[value]}
		/>
	);
};

export const replaceClientNameMergeTag = (text = '', client) => {
	if (!text) return '';

	return text.replace(/{{client\.name}}/g, client?.name || '');
};

export const ComposeDetailsSection = () => {
	const [showCC, setShowCC] = useState(false);
	const [showBCC, setShowBCC] = useState(false);
	const { isMetadataExpanded, toggleMetadata } = useUnlayerUIContext();
	const {
		handleTemplateSelectorOpen,
		isFetchingTemplates,
		selectedTemplateId,
		templateActionError,
		templateSummaries,
	} = useUnlayerTemplateContext();
	const {
		canUseDraftContext,
		draftSummaries,
		handleDraftSelectorOpen,
		isAnyDraftActionLoading,
		isFetchingDrafts,
		selectedDraftId,
		setIsDeleteDraftDialogOpen,
	} = useUnlayerDraftContext();
	const { handleClientChange, handleDealChange, handleDraftSelect, handleTemplateSelect } =
		useUnlayerComposeInteractionsContext();
	const {
		delegatedEmails,
		handleSignatureSelect,
		isFetchingSignature,
		isSignatureDisabled,
		selectedSignature,
		signatureDisabledReason,
		signatureError,
		signatureOptions,
	} = useUnlayerSignatureContext();
	const { values, errors, touched, setFieldValue, setFieldTouched, isSubmitting } =
		useFormikContext();

	const selectedClientId = values.client?.clientId ?? null;
	const selectedDealId = values.deal?.dealId ?? null;
	const draftDropdownDisabled = isSubmitting || !canUseDraftContext;
	const draftsDropdownTooltip = canUseDraftContext
		? ''
		: 'Select a client or deal to view drafts.';
	const [isPreviewMode, setIsPreviewMode] = useState(false);
	const finalTemplateSubject = useMemo(() => {
		return values.subject?.includes('{{client.name}}')
			? replaceClientNameMergeTag(values.subject, values.client)
			: values.subject;
	}, [values.subject, values.client]);

	return (
		<>
			<FormikNoteObject />
			<Box
				sx={{
					height: 'auto',
					flexShrink: 0,
					border: '1px solid',
					borderColor: 'divider',
					borderRadius: 1,
					p: 2,
					overflow: 'visible',
				}}
			>
				<Stack
					direction='row'
					alignItems='center'
					justifyContent='space-between'
					sx={{ mb: isMetadataExpanded ? 2 : 0 }}
				>
					<Typography variant='broker_header'>Compose Details</Typography>
					<IconButton
						size='small'
						onClick={toggleMetadata}
						aria-expanded={isMetadataExpanded}
						aria-label={
							isMetadataExpanded
								? 'Collapse compose details'
								: 'Expand compose details'
						}
					>
						{isMetadataExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
				</Stack>

				<Collapse in={isMetadataExpanded} timeout='auto'>
					<Grid container spacing={1}>
						<Grid size={{ xs: 12, md: 6 }}>
							<FormControl fullWidth error={touched.client && Boolean(errors.client)}>
								<FormLabel>Client</FormLabel>
								<InstantClientSelect
									clientId={selectedClientId}
									clientName={values.client?.name}
									setClient={handleClientChange}
									width='100%'
								/>
								{touched.client && errors.client && (
									<FormHelperText error>{errors.client}</FormHelperText>
								)}
							</FormControl>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<FormControl fullWidth>
								<FormLabel>Associated deal or claim</FormLabel>
								<InstantDealSelect
									disabled={!selectedClientId}
									clientId={selectedClientId}
									dealId={selectedDealId}
									setDeal={handleDealChange}
									autoPopulate={true}
								/>
							</FormControl>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<FormControl fullWidth>
								<FormLabel>From</FormLabel>
								<UserEmailSelect
									value={values.from}
									delegatedEmails={delegatedEmails}
									onChange={(value) => setFieldValue('from', value)}
								/>
							</FormControl>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<FormControl fullWidth error={touched.to && Boolean(errors.to)}>
								<Stack
									direction='row'
									justifyContent='space-between'
									alignItems='center'
								>
									<FormLabel>To</FormLabel>

									<Stack direction='row'>
										{!showCC && (
											<Tooltip
												title={
													values.cc.length > 0
														? values.cc
																.map((c) => c.contactEmail)
																.join(', ')
														: ''
												}
											>
												<Badge
													badgeContent={values.cc.length}
													color='primary'
												>
													<Button
														variant='text'
														size='small'
														sx={{ textTransform: 'none' }}
														onClick={() => setShowCC(true)}
													>
														Cc
													</Button>
												</Badge>
											</Tooltip>
										)}

										{!showBCC && (
											<Tooltip
												title={
													values.bcc.length > 0
														? values.bcc
																.map((c) => c.contactEmail)
																.join(', ')
														: ''
												}
											>
												<Badge
													badgeContent={values.bcc.length}
													color='secondary'
												>
													<Button
														variant='text'
														size='small'
														sx={{ textTransform: 'none' }}
														onClick={() => setShowBCC(true)}
													>
														Bcc
													</Button>
												</Badge>
											</Tooltip>
										)}

										{showCC && showBCC && (
											<Button
												variant='text'
												size='small'
												sx={{ textTransform: 'none' }}
												onClick={() => {
													setShowCC(false);
													setShowBCC(false);
												}}
											>
												Hide
											</Button>
										)}
									</Stack>
								</Stack>

								<InstantClientSelect
									showContact={true}
									attendees={values.to}
									setClient={(contacts) => {
										setFieldValue(
											'to',
											Array.isArray(contacts) ? contacts : [],
										);
										setFieldTouched('to', true, true);
									}}
								/>

								{touched.to && errors.to && (
									<FormHelperText error>{errors.to}</FormHelperText>
								)}
							</FormControl>
						</Grid>
						{showCC && (
							<Grid size={{ xs: 12, md: 6 }}>
								<FormControl fullWidth sx={{ width: '100%' }}>
									<FormLabel>{'Cc'}</FormLabel>
									<ContactSelection value={'cc'} />
								</FormControl>
							</Grid>
						)}

						{showBCC && (
							<Grid size={{ xs: 12, md: 6 }}>
								<FormControl fullWidth sx={{ width: '100%' }}>
									<FormLabel>{'Bcc'}</FormLabel>
									<ContactSelection value={'bcc'} />
								</FormControl>
							</Grid>
						)}
						<Grid size={{ xs: 12, md: 6 }}>
							<FormControl fullWidth>
								<FormLabel>Template</FormLabel>
								<Autocomplete
									fullWidth
									options={templateSummaries}
									loading={isFetchingTemplates}
									value={
										templateSummaries.find(
											(template) => template.id === selectedTemplateId,
										) ?? null
									}
									onOpen={handleTemplateSelectorOpen}
									onChange={(_, template) =>
										handleTemplateSelect(template?.id ?? '')
									}
									getOptionLabel={(template) => template?.name ?? ''}
									isOptionEqualToValue={(option, value) => option.id === value.id}
									noOptionsText='No templates found'
									renderInput={(params) => (
										<TextField {...params} placeholder='Search templates' />
									)}
									renderOption={(props, template) => (
										<li {...props} key={template.id}>
											{template.name}
										</li>
									)}
								/>
							</FormControl>
						</Grid>
						{templateActionError.length > 0 && (
							<Grid size={12}>
								<FormHelperText error>{templateActionError}</FormHelperText>
							</Grid>
						)}
						<Grid size={{ xs: 12, md: 6 }}>
							<Tooltip
								title={draftsDropdownTooltip}
								disableHoverListener={draftsDropdownTooltip.length === 0}
							>
								<span>
									<FormControl fullWidth>
										<Stack
											direction='row'
											justifyContent='space-between'
											alignItems='center'
										>
											<FormLabel>Draft</FormLabel>

											{selectedDraftId && (
												<Button
													variant='text'
													color='error'
													size='small'
													disabled={
														isSubmitting || isAnyDraftActionLoading
													}
													onClick={() => setIsDeleteDraftDialogOpen(true)}
													sx={{ textTransform: 'none' }}
												>
													Delete Draft
												</Button>
											)}
										</Stack>

										<Select
											fullWidth
											displayEmpty
											value={selectedDraftId}
											disabled={draftDropdownDisabled}
											onOpen={handleDraftSelectorOpen}
											onChange={(event) =>
												handleDraftSelect(event.target.value)
											}
										>
											{draftSummaries.length > 0 ? (
												<MenuItem value=''>None</MenuItem>
											) : (
												<MenuItem value='' sx={{ display: 'none' }} />
											)}

											{isFetchingDrafts && (
												<MenuItem disabled value='loading'>
													Loading drafts...
												</MenuItem>
											)}

											{draftSummaries.map((draft) => {
												const label = draft.subject || '(untitled draft)';

												const timestamp = formatDraftTimestamp(
													draft.createdWhen ?? draft.lastUpdated,
												);

												return (
													<MenuItem key={draft.id} value={draft.id}>
														{timestamp
															? `${label} ${timestamp}`
															: label}
													</MenuItem>
												);
											})}
										</Select>
									</FormControl>
								</span>
							</Tooltip>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<FormControl
								fullWidth
								error={touched.subject && Boolean(errors.subject)}
							>
								<Stack
									direction='row'
									alignItems='center'
									justifyContent='space-between'
								>
									<FormLabel>Subject</FormLabel>
									<Button
										size='small'
										onClick={() => setIsPreviewMode(!isPreviewMode)}
										sx={{ minWidth: '80px' }}
									>
										{isPreviewMode ? 'Edit' : 'Preview'}
									</Button>
								</Stack>

								{isPreviewMode ? (
									<TextField
										disabled
										value={
											finalTemplateSubject || 'No subject preview available'
										}
										sx={{
											'& .MuiInputBase-root': {
												backgroundColor: 'action.hover',
											},
										}}
									/>
								) : (
									<TextField
										placeholder='Subject'
										value={values.subject}
										onChange={(event) =>
											setFieldValue('subject', event.target.value)
										}
										onBlur={() => setFieldTouched('subject', true, true)}
									/>
								)}

								{touched.subject && errors.subject && (
									<FormHelperText error>{errors.subject}</FormHelperText>
								)}
							</FormControl>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<Tooltip
								title={signatureDisabledReason}
								disableHoverListener={signatureDisabledReason.length === 0}
							>
								<span>
									<FormControl fullWidth>
										<FormLabel>Signature</FormLabel>
										<UserEmailSignatureSelect
											emailSignatures={signatureOptions}
											value={selectedSignature}
											disabled={isSubmitting || isSignatureDisabled}
											loading={isFetchingSignature}
											onChange={handleSignatureSelect}
										/>
										{signatureError.length > 0 && (
											<FormHelperText error>{signatureError}</FormHelperText>
										)}
									</FormControl>
								</span>
							</Tooltip>
						</Grid>
						<Grid size={12}>
							<FileUpload
								files={values.files}
								setFiles={(value) => setFieldValue('files', value)}
								loading={isSubmitting}
								attachFromSharepoint={selectedDraftId || values.client}
							/>
						</Grid>
					</Grid>
				</Collapse>
			</Box>
		</>
	);
};
